import React, { forwardRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/core/utils';

interface SettingsCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  children: React.ReactNode;
  variant?: 'default' | 'destructive';
  className?: string;
  /** Optional element to render on the right side of the header (e.g., status badge) */
  headerAction?: React.ReactNode;
  /** ID for section navigation */
  id?: string;
}

export const SettingsCard = forwardRef<HTMLDivElement, SettingsCardProps>(({
  icon: Icon,
  title,
  description,
  children,
  variant = 'default',
  className,
  headerAction,
  id,
}, ref) => (
  <Card
    ref={ref}
    id={id}
    className={cn(variant === 'destructive' && 'border-destructive', className)}
  >
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
      <div className="flex-1">
        <CardTitle className={cn(
          "text-sm font-medium flex items-center gap-2",
          variant === 'destructive' && 'text-destructive'
        )}>
          <Icon className={cn("h-5 w-5", variant === 'destructive' ? '' : 'text-primary')} />
          {title}
        </CardTitle>
        <CardDescription className="mt-1">
          {description}
        </CardDescription>
      </div>
      {headerAction && (
        <div className="flex-shrink-0 ml-4">
          {headerAction}
        </div>
      )}
    </CardHeader>
    <CardContent className="p-4">
      {children}
    </CardContent>
  </Card>
));

SettingsCard.displayName = 'SettingsCard';